// ============================================================================
// TASK CARD OVERLAY - Running session tasks as a vertical list on the right 20%
// Spinning cog while running → green tick on complete → fades out.
// ============================================================================

import React, { useEffect, useState, useRef } from 'react';
import { Settings, CheckCircle, XCircle } from 'lucide-react';
import { useSecondScreenData } from '../../contexts/SecondScreenContext';
import type { SessionTaskSummary, TaskStatus } from '../../types/session-task.types';

const FADE_IN_MS = 600;
const FADE_OUT_MS = 1500;
const COMPLETE_LINGER_MS = 6_000;
const SAFETY_DISMISS_MS = 5 * 60_000;

const ACTIVE_STATUSES: TaskStatus[] = ['running', 'pending', 'waiting_human'];
const TERMINAL_STATUSES: TaskStatus[] = ['completed', 'failed'];

function accentForStatus(status: TaskStatus): string {
  if (status === 'completed') return '#22c55e';
  if (status === 'failed') return '#ef4444';
  return '#00c7c4';
}

function subtitleForStatus(status: TaskStatus): string {
  if (status === 'running') return 'Running...';
  if (status === 'pending') return 'Queued...';
  if (status === 'waiting_human') return 'Waiting for input...';
  if (status === 'completed') return 'Complete';
  if (status === 'failed') return 'Failed';
  return status;
}

// ── Pure helper: determine which tasks to render ──────────────────────────────

function isTaskRenderable(
  task: SessionTaskSummary,
  dismissed: Set<string>,
  known: Map<string, TaskStatus>,
  now: number,
): boolean {
  if (dismissed.has(task.workflowId)) return false;
  const isActive = ACTIVE_STATUSES.includes(task.status);
  const startedAt = task.startedAt ? new Date(task.startedAt).getTime() : 0;
  const isRecentTerminal =
    TERMINAL_STATUSES.includes(task.status) &&
    (known.has(task.workflowId) || (startedAt > 0 && now - startedAt < RECENT_TASK_WINDOW_MS));
  return isActive || isRecentTerminal;
}

function isStaleActiveTask(
  id: string,
  dismissed: Set<string>,
  seenIds: Set<string>,
  known: Map<string, TaskStatus>,
): boolean {
  if (seenIds.has(id) || dismissed.has(id)) return false;
  const lastStatus = known.get(id);
  return !!lastStatus && ACTIVE_STATUSES.includes(lastStatus);
}

function buildRenderableTasks(
  activeTasks: SessionTaskSummary[],
  dismissed: Set<string>,
  known: Map<string, TaskStatus>,
  lastKnown: Map<string, SessionTaskSummary>,
  now: number,
): SessionTaskSummary[] {
  const result: SessionTaskSummary[] = [];
  const seenIds = new Set<string>();

  for (const task of activeTasks) {
    if (!isTaskRenderable(task, dismissed, known, now)) continue;
    result.push(task);
    seenIds.add(task.workflowId);
  }

  for (const [id, task] of lastKnown) {
    if (!isStaleActiveTask(id, dismissed, seenIds, known)) continue;
    result.push(task);
  }

  return result;
}

// ============================================================================
// Single card
// ============================================================================

interface SingleTaskCardProps {
  task: SessionTaskSummary;
  onDismiss: (workflowId: string) => void;
}

const SingleTaskCard: React.FC<SingleTaskCardProps> = ({ task, onDismiss }) => {
  const [opacity, setOpacity] = useState(0);
  const [currentStatus, setCurrentStatus] = useState<TaskStatus>(task.status);
  const fadeOutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const safetyRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fade in on mount
  useEffect(() => {
    const raf = requestAnimationFrame(() => setOpacity(1));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Safety-net dismiss for stuck running cards
  useEffect(() => {
    safetyRef.current = setTimeout(() => onDismiss(task.workflowId), SAFETY_DISMISS_MS);
    return () => { if (safetyRef.current) clearTimeout(safetyRef.current); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // React to status change (running → completed/failed)
  useEffect(() => {
    if (task.status === currentStatus) return;
    setCurrentStatus(task.status);

    const isTerminalNow = TERMINAL_STATUSES.includes(task.status);
    if (isTerminalNow) {
      if (safetyRef.current) clearTimeout(safetyRef.current);
      fadeOutRef.current = setTimeout(() => setOpacity(0), COMPLETE_LINGER_MS);
      dismissRef.current = setTimeout(() => onDismiss(task.workflowId), COMPLETE_LINGER_MS + FADE_OUT_MS);
    }
  }, [task.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup
  useEffect(() => {
    return () => {
      if (fadeOutRef.current) clearTimeout(fadeOutRef.current);
      if (dismissRef.current) clearTimeout(dismissRef.current);
      if (safetyRef.current) clearTimeout(safetyRef.current);
    };
  }, []);

  const handleClick = () => {
    setOpacity(0);
    setTimeout(() => onDismiss(task.workflowId), FADE_OUT_MS);
  };

  const accent = accentForStatus(currentStatus);
  const isTerminal = TERMINAL_STATUSES.includes(currentStatus);

  let statusIcon: React.ReactNode;
  if (currentStatus === 'completed') {
    statusIcon = <CheckCircle size={36} style={{ color: accent }} />;
  } else if (currentStatus === 'failed') {
    statusIcon = <XCircle size={36} style={{ color: accent }} />;
  } else {
    statusIcon = <Settings size={36} style={{ color: accent }} className={!isTerminal ? 'animate-spin' : ''} />;
  }

  return (
    <div
      className="flex items-center gap-5 cursor-pointer w-full"
      style={{
        opacity,
        transition: `opacity ${opacity === 0 ? FADE_OUT_MS : FADE_IN_MS}ms ease`,
        pointerEvents: opacity > 0 ? 'auto' : 'none',
      }}
      onClick={handleClick}
    >
      {/* Icon circle */}
      <div className="relative shrink-0">
        <div
          className="w-20 h-20 rounded-full border-[3px] flex items-center justify-center"
          style={{
            backgroundColor: accent + '1a',
            borderColor: accent + '66',
            boxShadow: `0 0 24px 4px ${accent}33`,
          }}
        >
          {statusIcon}
        </div>
      </div>

      {/* Text */}
      <div
        className="flex-1 rounded-2xl bg-black/60 backdrop-blur-sm border px-5 py-4"
        style={{ borderColor: accent + '33' }}
      >
        <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: accent }}>
          {task.taskCategory === 'thinking' ? 'Thinking' : 'Working'}
        </p>
        <p className="text-white font-medium text-sm leading-snug line-clamp-2">{task.taskName}</p>
        <p className="text-white/50 text-xs mt-1">{subtitleForStatus(currentStatus)}</p>
      </div>
    </div>
  );
};

// ============================================================================
// Overlay — fixed right 20% column
// ============================================================================

const RECENT_TASK_WINDOW_MS = 5 * 60_000;
const MAX_CARDS = 8;

const TaskCardOverlay: React.FC = () => {
  const { state } = useSecondScreenData();
  const [, forceUpdate] = useState(0);

  const knownRef = useRef<Map<string, TaskStatus>>(new Map());
  const dismissedRef = useRef<Set<string>>(new Set());
  const lastKnownTasksRef = useRef<Map<string, SessionTaskSummary>>(new Map());

  const activeTasks = state?.activeTasks ?? [];
  const now = Date.now();

  // Update last-known snapshot
  for (const task of activeTasks) {
    lastKnownTasksRef.current.set(task.workflowId, task);
  }

  const renderableTasks = buildRenderableTasks(
    activeTasks,
    dismissedRef.current,
    knownRef.current,
    lastKnownTasksRef.current,
    now,
  );

  // Register / update known status
  for (const task of activeTasks) {
    if (ACTIVE_STATUSES.includes(task.status) || knownRef.current.has(task.workflowId)) {
      knownRef.current.set(task.workflowId, task.status);
    }
  }

  const handleDismiss = (workflowId: string) => {
    knownRef.current.delete(workflowId);
    dismissedRef.current.add(workflowId);
    forceUpdate(n => n + 1);
  };

  const visible = renderableTasks.slice(0, MAX_CARDS);

  if (visible.length === 0) return null;

  return (
    <div
      className="fixed top-0 right-0 h-full z-[85] pointer-events-none flex flex-col justify-start gap-3 p-4 pt-8"
      style={{ width: '20%' }}
    >
      {visible.map(task => (
        <div key={task.workflowId} className="pointer-events-auto w-full">
          <SingleTaskCard
            task={task}
            onDismiss={handleDismiss}
          />
        </div>
      ))}
    </div>
  );
};

export default TaskCardOverlay;
